package nl.volleyplanner.web.controller.planner.rulesets_config_v2

import kotlinx.browser.document
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.html.Entities
import kotlinx.html.FlowContent
import kotlinx.html.a
import kotlinx.html.button
import kotlinx.html.classes
import kotlinx.html.div
import kotlinx.html.dom.create
import kotlinx.html.h3
import kotlinx.html.id
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import kotlinx.html.onClick
import kotlinx.html.role
import kotlinx.html.span
import kotlinx.html.stream.createHTML
import nl.volleyplanner.api.planner.Ruleset
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.CoroutineManager.RENDER_SCOPE
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.ControllerContext
import nl.volleyplanner.web.base.IconPosition
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.BootstrapButton
import nl.volleyplanner.web.base.component.ButtonVariant
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.ModalComponent
import nl.volleyplanner.web.base.component.ModalSize
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.planner.rulesets_config.GroupConstraintType
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.lib.Tab
import nl.volleyplanner.web.util.BootstrapColor
import nl.volleyplanner.web.util.combineAndLaunch
import nl.volleyplanner.web.util.map
import org.w3c.dom.HTMLElement
import org.w3c.dom.get
import kotlin.uuid.Uuid

class RulesetTemplateEditorComponent(
    private val dataManager: TeamPouleConfigDataManager
) : ModalComponent(
    "Templates bewerken",
    modalSize = ModalSize.Large
) {
    private val _this = this

    val saveStatus = MutableStateFlow<String>("")
    // TODO: Better setup where components can be created directly with flows for the data
    //  But this likely also requires stateflows to be passed to render methods. We are not there yet
    var editorComponents = emptyList<RulesetTemplateEditorFormComponent>()
    val rulesets = dataManager.availableRulesets

    override val footerButtons: List<BootstrapButton> = listOf(
        BootstrapButton("Opslaan", ButtonVariant.Primary, { event ->
            event.preventDefault()
            val existingRulesetIds = dataManager.availableRulesets.value.map { it.identifier }.toSet()

            val listGroupElement = document.getElementById("vp-ruleset-template-tab")!!
            val rulesetsToUpdate = listGroupElement.querySelectorAll("a")
                .map { it as HTMLElement }
                .mapNotNull { it.getAttribute("data-vp-editor-id") }
                .map { editorId -> editorComponents.first { it.componentId == editorId.toInt()} }
                .filter {
                    // Filter rulesets which are deleted and for which have no DB record
                    // i.e. rulesets that were created and immediately deleted before saving
                    !it.rulesetFlow.value.archived || existingRulesetIds.contains(it.rulesetFlow.value.identifier)
                }
                .mapIndexed { index, editorComponent ->
                    editorComponent.rulesetFlow.value.copy(priority = index + 1)
                }
            // Ensure we have a name and identifier
            if (rulesetsToUpdate.any { ruleset -> ruleset.identifier.isEmpty() || ruleset.name.isEmpty() }) {
                saveStatus.value = "Failed"
            } else {
                saveStatus.value = "Saving"
                launchAsync(CoroutineManager.DISPATCHER_SCOPE) {
                    dataManager.updateRulesets(rulesetsToUpdate)
                }.onComplete {
                    saveStatus.value = "Success"
                    hide()
                }.onError {
                    saveStatus.value = "Failed"
                }
            }
        }),
        BootstrapButton("Annuleren", ButtonVariant.Secondary, { event ->
            event.preventDefault()
            hide()
        })
    )

    override fun initialize() {
        super.initialize()
//        val triggerList = document.querySelectorAll("#vp-ruleset-template-tab a")
//        for (i in 0 until triggerList.length) {
//            val triggerElement = triggerList[i] as HTMLElement
//            val bootstrapTabTrigger = Tab(triggerElement)
//            triggerElement.addEventListener("click", { event ->
//                event.preventDefault()
//                bootstrapTabTrigger.show()
//            })
//        }
    }

    private fun getCreationRuleset() = Ruleset(
        identifier = "", name = "", description = "", priority = 0, archived = false,
        refereeRequiredAmount = 0, scoreKeeperRequiredAmount = 0,
        emptyList(), emptyList(), emptyList(), emptyList()
    )

    private fun FlowContent.editorComponentNavItem(editorComponent: RulesetTemplateEditorFormComponent, isActive: Boolean = false) {
        val ruleset = editorComponent.ruleset
        a(classes = "list-group-item list-group-item-action") {
            if (isActive) classes += "active"
            id = "vp-${componentId}-ruleset-template-${editorComponent.componentId}-list"
            attributes["data-bs-toggle"] = "list"
            attributes["data-vp-editor-id"] = editorComponent.componentId.toString()
            href = "#vp-ruleset-template-${editorComponent.componentId}"
            role = "tab"
            attributes["aria-controls"] = "vp-ruleset-template-${editorComponent.componentId}"

            onClickFunction = { event ->
                event.preventDefault()
                Tab.getOrCreateInstance(event.currentTarget as HTMLElement)
                    .show()
            }

            component(ListeningComponent("RulesetItemSelector", listOf(editorComponent.rulesetFlow)) {
                val updatedRuleset = editorComponent.rulesetFlow.value
                span {
                    if (updatedRuleset.archived) {
                        classes += "text-decoration-line-through"
                    }
                    if (updatedRuleset != ruleset) {
                        classes += "fst-italic"
                    }
                    +(updatedRuleset.name.takeIf { it.isNotBlank() } ?: "Nieuw template")
                    if (updatedRuleset != ruleset) {
                        +"*"
                    }
                }
            }, _this)
        }
    }

    private fun FlowContent.rulesetEditorPaneItem(editorComponent: RulesetTemplateEditorFormComponent, isActive: Boolean = false) {
        val ruleset = editorComponent.ruleset
        div(classes = "tab-pane fade") {
            if (isActive) classes += listOf("show", "active")
            id = "vp-ruleset-template-${editorComponent.componentId}"
            role = "tabpanel"
            attributes["aria-labelledby"] = "vp-${componentId}-ruleset-template-${editorComponent.componentId}-list"

            component(editorComponent, _this)
        }
    }

    override fun FlowContent.renderBody() {
        if (dataManager.refereeGroupConstraintsFlow.value.isEmpty() || dataManager.scoreKeeperGroupConstraintsFlow.value.isEmpty()) {
            spinner()
            return
        }
        val supportedRefereeConstraints = dataManager.refereeGroupConstraintsFlow.value.groupBy {
            GroupConstraintType.fromConstraintGroup(
                it
            )
        }
        val supportedScoreKeeperConstraints = dataManager.scoreKeeperGroupConstraintsFlow.value.groupBy {
            GroupConstraintType.fromConstraintGroup(
                it
            )
        }

        editorComponents = rulesets.value.map { ruleset ->
            RulesetTemplateEditorFormComponent(ruleset, supportedRefereeConstraints, supportedScoreKeeperConstraints)
        }
        component(ListeningComponent("RulesetEditorStatus", listOf(saveStatus)) {
            div(classes = "border p-2") {
                if (saveStatus.value.isEmpty()) {
                    classes += "d-none"
                }
                when(saveStatus.value) {
                    "Saving" -> {
                        classes += setOf("border-secondary")
                        spinner()
                        +" Wijzigingen opslaan..."
                    }
                    "Success" -> {
                        classes += setOf("border-success", "bg-success-subtle")
                        +"Opslaan gelukt"
                    }
                    "Failed" -> {
                        classes += setOf("border-danger", "bg-danger-subtle")
                        +"Er is iets fout gegaan tijdens het opslaan..."
                    }
                    else -> {
                        classes += setOf("border-secondary")
                    }
                }
            }
        })

        div(classes = "row") {
            div(classes = "col-4") {
                div(classes = "d-flex mb-1") {
                    button(classes = "btn btn-sm btn-outline-secondary me-1") {
                        onClickFunction = { event ->
                            event.preventDefault()
                            val listGroupElement = document.getElementById("vp-ruleset-template-tab")!!
                            val activeElement = listGroupElement.querySelector("a.active")!!

                            activeElement.previousElementSibling?.before(activeElement)
                        }
                        icon("bi-arrow-up-square-fill")
                    }
                    button(classes = "btn btn-sm btn-outline-secondary") {
                        onClickFunction = { event ->
                            event.preventDefault()
                            val listGroupElement = document.getElementById("vp-ruleset-template-tab")!!
                            val activeElement = listGroupElement.querySelector("a.active")!!

                            activeElement.nextElementSibling
                                // Only go down further if not the last element (which is the add new template)
                                ?.takeIf { it.nextElementSibling != null }
                                ?.after(activeElement)
                        }
                        icon("bi-arrow-down-square-fill")
                    }
                }
                div(classes = "list-group") {
                    id = "vp-ruleset-template-tab"
                    role = "tablist"

                    editorComponents.forEachIndexed { index, editorComponent ->
                        editorComponentNavItem(editorComponent, index == 0)
                    }

                    a(classes = "list-group-item list-group-item-action text-primary bg-secondary-subtle") {
                        id = "vp-${componentId}-ruleset-template-new-list"
                        //attributes["data-bs-toggle"] = "list"
                        href = "#vp-ruleset-template-new"
                        role = "button"
                        attributes["aria-controls"] = "vp-ruleset-template-new"

                        onClickFunction = { event ->
                            event.preventDefault()
                            RENDER_SCOPE.async {
                                val newEditorComponent = RulesetTemplateEditorFormComponent(getCreationRuleset(), supportedRefereeConstraints, supportedScoreKeeperConstraints)
                                ControllerContext.withController(owningController) {
                                    val navWrapperElement =
                                        document.create.div { editorComponentNavItem(newEditorComponent, false) }
                                    val formPaneElement =
                                        document.create.div { rulesetEditorPaneItem(newEditorComponent, false) }

                                    (event.currentTarget as HTMLElement)
                                        .let {
                                            it.parentNode!!
                                                .insertBefore(
                                                    navWrapperElement.firstChild!!, it
                                                )
                                        }

                                    document.getElementById("vp-ruleset-template-tabContent")!!
                                        .appendChild(formPaneElement.firstChild!!)

                                    document.getElementById("vp-${componentId}-ruleset-template-${newEditorComponent.componentId}-list")!!
                                        .let { Tab.getOrCreateInstance(it).show() }
                                    editorComponents = editorComponents + newEditorComponent
                                }
                            }
                        }

                        icon("bi-plus-square-fill", color = BootstrapColor.Primary)
                        +" Nieuwe template"
                    }
                }
            }
            div(classes = "col-8") {
                div(classes = "tab-content") {
                    id = "vp-ruleset-template-tabContent"

                    editorComponents.forEachIndexed { index, editorComponent ->
                        rulesetEditorPaneItem(editorComponent, index == 0)
                    }
                }
            }
        }
    }

    override suspend fun getState() = dataManager.refereeGroupConstraintsFlow.combineAndLaunch(RENDER_SCOPE, dataManager.scoreKeeperGroupConstraintsFlow)
}